import { Lock, ArrowRight, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ReactNode } from "react";
import { isAdminEmail } from "@/lib/subscriptionContext";
import { useAuth } from "@/lib/authContext";

interface LockedSectionProps {
  children: ReactNode;
  title?: string;
  description?: string;
  requiredPlan?: string;
}

export default function LockedSection({ children, title = "Unlock this feature", description, requiredPlan = "Pro" }: LockedSectionProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (isAdminEmail(user?.email)) return <>{children}</>;

  return (
    <div className="relative rounded-2xl overflow-hidden border border-border/40">
      {/* Blurred preview of the actual content */}
      <div className="blur-[3px] opacity-70 pointer-events-none select-none transition-all duration-300">
        {children}
      </div>

      {/* Semi-transparent overlay with gradient fade */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background/80 z-10" />

      {/* CTA overlay */}
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6">
        <div className="bg-card/90 backdrop-blur-sm border border-border/50 rounded-xl p-6 max-w-sm shadow-lg">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Eye className="w-5 h-5 text-primary" />
            <Lock className="w-4 h-4 text-muted-foreground" />
          </div>
          <h3 className="font-heading font-medium text-foreground text-base mb-1.5">{title}</h3>
          {description && <p className="text-sm text-muted-foreground mb-4">{description}</p>}
          {!description && (
            <p className="text-sm text-muted-foreground mb-4">
              This is a preview. Upgrade to {requiredPlan} for full access to this feature.
            </p>
          )}
          <Button size="sm" className="gap-2 w-full" onClick={() => navigate("/pricing")}>
            Upgrade to {requiredPlan} <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
